'use client'

import { Logger } from '@knockaway/foundation-web-logger'
import { useConsentCheck } from '@phoenix-web/react-hooks'
import {
  APP_ENV,
  APP_IS_LOCAL,
  APP_NAME,
  APP_REV,
  COOKIE_YES_CONSENT_COOKIE,
  DD_APPLICATION_ID,
  DD_CLIENT_TOKEN,
  DD_ENABLED,
  KNOCK_PRIVACY_COOKIE,
} from '@/constants'
import type { DatadogLogsEnv, DatadogLogsInitProps, DatadogRumInitProps } from '@knockaway/foundation-web-logger'

const ddCommonConfig = {
  env: `${APP_ENV}` as DatadogLogsEnv,
  clientToken: `${DD_CLIENT_TOKEN}`,
  version: `${APP_REV}`,
  service: `${APP_NAME || 'courtyard2'}`,
}
const ddLogsConfig = {
  ...ddCommonConfig,
  telemetrySampleRate: 0, // Opt out of telemetry collection
  beforeSend: (event, context) => {
    if (event?.origin === 'source' && event?.message?.includes('Uncaught "Object Not Found Matching Id:')) {
      return false
    } else if (context && 'isAborted' in context && context.isAborted) {
      return false
    } else if (
      event?.origin === 'network' &&
      (event?.message?.includes('XHR error') || event?.message?.includes('Fetch error'))
    ) {
      event.status = 'warn'
      if (event?.http?.status_code === 0) {
        // 0 code indicates error that happened even before the server is contacted.
        // Possibilities: Illegal cross-origin request (CORS); Firewall block or filtering;
        //   The request itself was cancelled in code; An installed browser extension is messing things up
        // https://fetch.spec.whatwg.org/#concept-network-error
        return false
      }
    }
    // remove any email or code from view url
    event.view.url = event.view.url.replace(/email=[^&]*/, 'email=REDACTED').replace(/code=[^&]*/, 'code=REDACTED')
    return event
  },
} as DatadogLogsInitProps
const ddRumConfig = {
  ...ddCommonConfig,
  applicationId: `${DD_APPLICATION_ID}`,
  allowedTracingUrls: [
    /^https:\/\/[^/]*(graphql|gql|courtyard|courtyard2|my)[^/]*\.knock\.com([/?#]|$)/,
    ...(APP_IS_LOCAL ? [/^https?:\/\/localhost:(4000|8000|5012)([/?#]|$)/] : []),
  ],
  beforeSend: (event) => {
    // Redact PII from view URL on every event type. Every RUM event (view,
    // action, error, resource, …) carries event.view.url, so we strip these
    // before any type-specific filtering. Mirrors the redaction already done
    // by ddLogsConfig.beforeSend above.
    if (event.view?.url) {
      event.view.url = event.view.url.replace(/email=[^&]*/, 'email=REDACTED').replace(/code=[^&]*/, 'code=REDACTED')
    }

    if (event.type !== 'error') return true
    const message = event.error?.message ?? ''
    // Microsoft Outlook SafeLink injects scripts that throw this on every page
    // load. Match the same `Uncaught "` prefix the Logs filter requires —
    // avoids false-positive drops of any legitimate error message that happens
    // to contain "Object Not Found Matching Id:".
    if (message.includes('Uncaught "Object Not Found Matching Id:')) return false
    // React's SSR streaming uses an internal $RS function to complete suspense
    // boundaries by reading the placeholder's parentNode. When a browser extension
    // (most commonly Google Translate) mutates the DOM, the placeholder loses its
    // parent and $RS throws. Stack always points to $RS in the streamed HTML,
    // never to app code. Strict match avoids dropping real app bugs.
    if (
      message === "Cannot read properties of null (reading 'parentNode')" &&
      (event.error?.stack?.includes('$RS') ?? false)
    ) {
      return false
    }
    // Ad/tracker blockers prevent heap_config.js from loading.
    if (message.includes('Failed to load heap_config.js')) return false
    // CookieYes consent banner (third-party script) calls .json() on responses
    // that sometimes return plain text, producing SyntaxErrors it console.errors.
    // The handling_stack points into cdn-cookieyes.com — not our code, not actionable.
    if (event.error?.handling_stack?.includes('cdn-cookieyes.com')) return false
    return true
  },
} as DatadogRumInitProps

export default function DatadogLogger() {
  const [hasNecessaryConsent] = useConsentCheck({
    cookieYesName: COOKIE_YES_CONSENT_COOKIE,
    knockPrivacyName: KNOCK_PRIVACY_COOKIE,
    categoryName: 'necessary',
  })
  const ddLogsConfigOptions = {
    ...ddLogsConfig,
    consent: hasNecessaryConsent ? 'granted' : 'not-granted',
  }
  const ddRumConfigOptions = {
    ...ddRumConfig,
    consent: hasNecessaryConsent ? 'granted' : 'not-granted',
  }
  return <>{DD_ENABLED && <Logger ddLogsConfig={ddLogsConfigOptions} ddRumConfig={ddRumConfigOptions} />}</>
}
